/**
 * The "Your photo" card: the player's own photo under the server's
 * transparent card, combined on the device. The photo never leaves it, so
 * the page draws only the photo and the wash; every word on the card comes
 * from the server's PNG, keeping the design in one place.
 */

import { CARD_SIZE, type CardSize } from "./cardChoice";

/** Where the crop sits on each axis: 0 is the left or top edge, 1 the other. */
export interface Focus {
    x: number;
    y: number;
}

export const CENTRE: Focus = { x: 0.5, y: 0.5 };

/** The card a photo is decoded to cover. */
const LARGEST_CARD = CARD_SIZE;

/**
 * The longest side a decoded photo is kept at. A panorama scaled to cover
 * the card would otherwise pass the canvas area iOS Safari allows.
 */
const MAX_PHOTO_SIDE = 4096;

/**
 * The design's navy wash, kept light: the transparent card's type carries
 * its own shadow, so more of the player's photo can show through.
 */
const WASH: [number, number][] = [
    [0, 0.35],
    [0.4, 0.45],
    [1, 0.85],
];

interface Size {
    width: number;
    height: number;
}

function coverScale(photo: Size, card: CardSize): number {
    return Math.max(card.width / photo.width, card.height / photo.height);
}

/** The photo's rectangle on the card: covering it, cropped at `focus`. */
export function coverRect(photo: Size, focus: Focus, card: CardSize) {
    const scale = coverScale(photo, card);
    const w = photo.width * scale;
    const h = photo.height * scale;
    return {
        x: (card.width - w) * focus.x,
        y: (card.height - h) * focus.y,
        w,
        h,
    };
}

/** Whether any of the photo hangs off the card, for a drag to bring in. */
export function canPan(photo: Size, card: CardSize): boolean {
    const { w, h } = coverRect(photo, CENTRE, card);
    return w - card.width > 0.5 || h - card.height > 0.5;
}

function clamp01(n: number): number {
    return Math.min(1, Math.max(0, n));
}

/**
 * The focus after dragging the photo by (dx, dy) card pixels. An axis with
 * nothing cropped off stays put.
 */
export function panFocus(
    photo: Size,
    focus: Focus,
    dx: number,
    dy: number,
    card: CardSize,
): Focus {
    const { w, h } = coverRect(photo, focus, card);
    const spareX = w - card.width;
    const spareY = h - card.height;
    return {
        x: spareX > 0.5 ? clamp01(focus.x - dx / spareX) : focus.x,
        y: spareY > 0.5 ? clamp01(focus.y - dy / spareY) : focus.y,
    };
}

function newCanvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    return canvas;
}

function context2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No 2D canvas");
    return ctx;
}

/**
 * Decode a picked photo the right way up, no bigger than the card needs.
 *
 * Phone photos run to 12–48 megapixels, so the full-size decode is dropped
 * as soon as it has been drawn down to the size that covers the card.
 */
export async function loadPhoto(file: Blob): Promise<ImageBitmap> {
    // `from-image` turns an iPhone photo by its EXIF orientation.
    const full = await createImageBitmap(file, {
        imageOrientation: "from-image",
    });
    const scale = Math.min(
        coverScale(full, LARGEST_CARD),
        MAX_PHOTO_SIDE / Math.max(full.width, full.height),
    );
    if (scale >= 1) return full;
    const canvas = newCanvas(
        Math.round(full.width * scale),
        Math.round(full.height * scale),
    );
    try {
        const ctx = context2d(canvas);
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(full, 0, 0, canvas.width, canvas.height);
        return await createImageBitmap(canvas);
    } finally {
        full.close();
        // iOS holds a canvas's memory until it's collected; this frees it now.
        canvas.width = 0;
        canvas.height = 0;
    }
}

/** Paint the card: photo, wash, then the server's card over both. */
export function drawCustomCard(
    ctx: CanvasRenderingContext2D,
    photo: ImageBitmap,
    overlay: ImageBitmap,
    focus: Focus,
    card: CardSize,
): void {
    const { x, y, w, h } = coverRect(photo, focus, card);
    ctx.clearRect(0, 0, card.width, card.height);
    ctx.drawImage(photo, x, y, w, h);
    const wash = ctx.createLinearGradient(0, 0, 0, card.height);
    for (const [stop, alpha] of WASH) {
        wash.addColorStop(stop, `rgba(22, 39, 77, ${alpha})`);
    }
    ctx.fillStyle = wash;
    ctx.fillRect(0, 0, card.width, card.height);
    ctx.drawImage(overlay, 0, 0, card.width, card.height);
}

/** A JPEG: a photo as PNG runs to several megabytes for no visible gain. */
export function exportCard(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) =>
                blob ? resolve(blob) : reject(new Error("Empty card image")),
            "image/jpeg",
            0.9,
        );
    });
}
